import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Creator } from './useCreators';

export interface CreatorGroup {
  id: string;
  primary_name: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  total_followers: number;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
  platforms: Array<'tiktok' | 'instagram' | 'youtube'>;
  creators: Creator[];
}

interface UseGroupedCreatorsProps {
  platform?: 'tiktok' | 'instagram' | 'youtube' | 'all';
  searchTerm?: string;
}

export const useGroupedCreators = ({ platform = 'all', searchTerm }: UseGroupedCreatorsProps = {}) => {
  return useQuery({
    queryKey: ['grouped-creators', platform, searchTerm],
    queryFn: async (): Promise<CreatorGroup[]> => {
      // First, get all creator groups with their linked creators
      const { data: groups, error: groupsError } = await supabase
        .from('creator_groups')
        .select('*')
        .order('total_followers', { ascending: false });

      if (groupsError) throw groupsError;

      // Then get all creators that are linked to groups
      let creatorsQuery = supabase
        .from('creators')
        .select('*')
        .not('creator_group_id', 'is', null);

      if (platform !== 'all') {
        creatorsQuery = creatorsQuery.eq('platform', platform);
      }

      const { data: groupedCreators, error: creatorsError } = await creatorsQuery;

      if (creatorsError) throw creatorsError;

      // Also get ungrouped creators
      let ungroupedQuery = supabase
        .from('creators')
        .select('*')
        .is('creator_group_id', null)
        .order('follower_count', { ascending: false });

      if (platform !== 'all') {
        ungroupedQuery = ungroupedQuery.eq('platform', platform);
      }

      if (searchTerm && searchTerm.trim().length > 0) {
        ungroupedQuery = ungroupedQuery.or(`username.ilike.%${searchTerm}%,display_name.ilike.%${searchTerm}%`);
      }

      const { data: ungroupedCreators, error: ungroupedError } = await ungroupedQuery.limit(50);

      if (ungroupedError) throw ungroupedError;

      // Map creators to groups
      const creatorsByGroupId: Record<string, Creator[]> = {};
      (groupedCreators || []).forEach(row => {
        const groupId = row.creator_group_id;
        if (!creatorsByGroupId[groupId]) {
          creatorsByGroupId[groupId] = [];
        }
        creatorsByGroupId[groupId].push({
          id: row.id,
          username: row.username,
          display_name: row.display_name || row.username || 'Unknown',
          avatar_url: row.avatar_url || '',
          follower_count: row.follower_count ?? 0,
          following_count: row.following_count ?? 0,
          video_count: row.video_count ?? 0,
          like_count: row.like_count ?? 0,
          platform: row.platform as 'tiktok' | 'instagram' | 'youtube',
          is_verified: row.is_verified ?? false,
          bio: row.bio || '',
          external_links: row.external_links as any,
          audience_demographics: row.audience_demographics as any,
          category: row.category,
          region: row.region,
          email: row.email,
          contact_phone: row.contact_phone,
          business_category: row.business_category,
          profile_url: row.profile_url,
        });
      });

      // Build grouped results
      const groupedResults: CreatorGroup[] = (groups || [])
        .filter(group => {
          // Filter out groups with no linked creators
          if (!(creatorsByGroupId[group.id] || []).length) return false;
          // Filter by search term
          if (searchTerm && searchTerm.trim().length > 0) {
            const term = searchTerm.toLowerCase();
            const matchesGroup = 
              group.primary_name.toLowerCase().includes(term) ||
              (group.display_name && group.display_name.toLowerCase().includes(term));
            const matchesCreators = (creatorsByGroupId[group.id] || []).some(c =>
              c.username.toLowerCase().includes(term) ||
              c.display_name.toLowerCase().includes(term)
            );
            return matchesGroup || matchesCreators;
          }
          return true;
        })
        .filter(group => {
          // Filter by platform
          if (platform !== 'all') {
            return (creatorsByGroupId[group.id] || []).some(c => c.platform === platform);
          }
          return true;
        })
        .map(group => {
          const creators = creatorsByGroupId[group.id] || [];
          const platforms = [...new Set(creators.map(c => c.platform))] as Array<'tiktok' | 'instagram' | 'youtube'>;
          
          return {
            id: group.id,
            primary_name: group.primary_name,
            display_name: group.display_name,
            avatar_url: group.avatar_url || (creators[0]?.avatar_url || null),
            bio: group.bio,
            total_followers: group.total_followers || creators.reduce((sum, c) => sum + c.follower_count, 0),
            is_verified: group.is_verified || creators.some(c => c.is_verified),
            created_at: group.created_at,
            updated_at: group.updated_at,
            platforms,
            creators,
          };
        });

      // Convert ungrouped creators to single-creator groups for unified display
      const ungroupedAsGroups: CreatorGroup[] = (ungroupedCreators || []).map(row => ({
        id: `ungrouped-${row.id}`,
        primary_name: row.display_name || row.username || 'Unknown Creator',
        display_name: row.display_name || row.username || 'Unknown Creator',
        avatar_url: row.avatar_url || '',
        bio: row.bio || '',
        total_followers: row.follower_count ?? 0,
        is_verified: row.is_verified ?? false,
        created_at: row.created_at,
        updated_at: row.updated_at,
        platforms: [row.platform as 'tiktok' | 'instagram' | 'youtube'],
        creators: [{
          id: row.id,
          username: row.username,
          display_name: row.display_name || row.username || 'Unknown',
          avatar_url: row.avatar_url || '',
          follower_count: row.follower_count ?? 0,
          following_count: row.following_count ?? 0,
          video_count: row.video_count ?? 0,
          like_count: row.like_count ?? 0,
          platform: row.platform as 'tiktok' | 'instagram' | 'youtube',
          is_verified: row.is_verified ?? false,
          bio: row.bio || '',
          external_links: row.external_links as any,
          audience_demographics: row.audience_demographics as any,
          category: row.category,
          region: row.region,
          email: row.email,
          contact_phone: row.contact_phone,
          business_category: row.business_category,
          profile_url: row.profile_url,
        }],
      }));

      // Combine and sort by total followers
      return [...groupedResults, ...ungroupedAsGroups]
        .sort((a, b) => b.total_followers - a.total_followers)
        .slice(0, 50);
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};
